import { Button } from "@win-reality/react-ui";
import React, { useCallback, useMemo, useState } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { ClientPagination } from "@/components/ui/Table/client-pagination";
import { cn } from "@/lib/utils";

import { EditableTableCell } from "./EditableTableCell";
import { useEditableTableState } from "./hooks/useEditableTableState";
import type { EditableTableProps, TableGroup } from "./types";

/**
 * Reusable editable table component with change tracking and inline editing
 *
 * Features:
 * - Inline cell editing (select, searchable-select, text)
 * - Visual feedback for edited cells
 * - Change tracking (only saves modified rows)
 * - Save/Cancel with confirmation
 * - Pagination support
 * - Grouped or flat data rendering
 * - Performance optimized with memoization
 *
 * @template TData - The type of data in each row
 */
export function EditableTable<TData>({
  data,
  columns,
  onSave,
  keyExtractor,
  renderGroupHeader,
  isGrouped = false,
  currentPage = 0,
  totalPages = 1,
  totalItems,
  itemsPerPage,
  onPageChange,
  itemLabel = "items",
  className,
  isSaving = false,
  onCancel,
}: EditableTableProps<TData>) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const editableFields = useMemo(
    () => columns.filter((col) => col.editable).map((col) => col.key),
    [columns],
  );

  const flatData = useMemo(() => {
    if (!isGrouped) {
      return data as TData[];
    }
    const groups = data as TableGroup<TData>[];
    return groups.flatMap((group) => group.rows);
  }, [data, isGrouped]);

  const {
    currentData,
    changedRows,
    totalChangedFields,
    updateCell,
    isFieldEdited,
    resetChanges,
    commitSave,
  } = useEditableTableState({
    initialData: flatData,
    keyExtractor,
    editableFields,
  });

  // Create a map for quick lookups of current data
  const currentDataMap = useMemo(() => {
    const map = new Map<string, TData>();
    currentData.forEach((row) => {
      map.set(keyExtractor(row), row);
    });
    return map;
  }, [currentData, keyExtractor]);

  // Reconstruct data structure (grouped or flat) with current edits
  const displayData = useMemo(() => {
    if (!isGrouped) {
      return currentData;
    }

    // Reconstruct grouped structure with edited data
    const groups = data as TableGroup<TData>[];
    return groups.map((group) => ({
      ...group,
      rows: group.rows.map((row) => {
        const key = keyExtractor(row);
        return currentDataMap.get(key) || row;
      }),
    }));
  }, [isGrouped, data, currentData, currentDataMap, keyExtractor]);

  const handleCellChange = useCallback(
    (rowKey: string, field: keyof TData) => (value: string) => {
      updateCell(rowKey, field, value);
    },
    [updateCell],
  );

  const handleSave = useCallback(async () => {
    if (changedRows.length === 0) return;

    await onSave(changedRows);
    // Commit saved rows to prevent UI reversion before refetch
    commitSave(changedRows);
  }, [changedRows, onSave, commitSave]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      setShowCancelDialog(true);
    }
  }, [onCancel]);

  const confirmCancel = useCallback(() => {
    resetChanges();
    setShowCancelDialog(false);
  }, [resetChanges]);

  const renderRows = () => {
    if (isGrouped) {
      const groups = displayData as TableGroup<TData>[];

      const hasData =
        groups.length > 0 &&
        groups.some((group) => group.rows && group.rows.length > 0);

      if (!hasData) {
        return (
          <TableRow key="no-data" clickable={false} className="border-gray-200">
            <TableCell colSpan={columns.length} className="text-center py-8">
              No data found
            </TableCell>
          </TableRow>
        );
      }

      return groups
        .filter((group) => group.rows && group.rows.length > 0)
        .flatMap((group) => {
          const headerRows = renderGroupHeader
            ? renderGroupHeader(group)
            : null;

          const dataRows = group.rows.map((row) => {
            const rowKey = keyExtractor(row);
            return (
              <TableRow
                key={rowKey}
                className="hover:bg-transparent border-b border-gray-200"
              >
                {columns.map((column) =>
                  column.renderCell ? (
                    <React.Fragment key={`${rowKey}-${String(column.key)}`}>
                      {column.renderCell(row)}
                    </React.Fragment>
                  ) : (
                    <EditableTableCell
                      key={`${rowKey}-${String(column.key)}`}
                      column={column}
                      value={row[column.key]}
                      isEdited={isFieldEdited(rowKey, column.key)}
                      onChange={handleCellChange(rowKey, column.key)}
                      row={row}
                    />
                  ),
                )}
              </TableRow>
            );
          });

          // Return array of elements (header rows first, then data rows)
          if (!headerRows) {
            return dataRows;
          }
          if (Array.isArray(headerRows)) {
            return [...headerRows, ...dataRows];
          }
          return [headerRows, ...dataRows];
        });
    }

    const rows = displayData as TData[];

    if (rows.length === 0) {
      return (
        <TableRow key="no-data" clickable={false} className="border-gray-200">
          <TableCell colSpan={columns.length} className="text-center py-8">
            No data found
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => {
      const rowKey = keyExtractor(row);
      return (
        <TableRow
          key={rowKey}
          className="hover:bg-transparent border-b border-gray-200"
        >
          {columns.map((column) =>
            column.renderCell ? (
              <React.Fragment key={`${rowKey}-${String(column.key)}`}>
                {column.renderCell(row)}
              </React.Fragment>
            ) : (
              <EditableTableCell
                key={`${rowKey}-${String(column.key)}`}
                column={column}
                value={row[column.key]}
                isEdited={isFieldEdited(rowKey, column.key)}
                onChange={handleCellChange(rowKey, column.key)}
                row={row}
              />
            ),
          )}
        </TableRow>
      );
    });
  };

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-4",
          changedRows.length > 0 && "pb-28",
          className,
        )}
      >
        <div className="rounded-md bg-card border border-gray-200 overflow-x-auto">
          <Table noWrapper>
            <TableHeader>
              <TableRow clickable={false} className="border-gray-200">
                {columns.map((column) => (
                  <TableHead
                    key={String(column.key)}
                    className={cn("bg-gray-50", column.widthClass)}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>{renderRows()}</TableBody>
          </Table>

          {onPageChange &&
            totalItems !== undefined &&
            itemsPerPage !== undefined && (
              <ClientPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                itemLabel={itemLabel}
              />
            )}
        </div>

        {changedRows.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
            <div className="mx-auto px-6 py-4">
              <div className="flex flex-col sm:flex-row justify-center sm:justify-end gap-3">
                <Button
                  variant="ghost"
                  className="w-full sm:w-auto"
                  onClick={handleCancel}
                  isDisabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary-flat-light"
                  className="w-full sm:w-auto"
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={isSaving}
                >
                  Save Changes ({totalChangedFields})
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Discard all changes?</DialogTitle>
            <DialogDescription>
              This will revert your edits and restore the original data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 pt-2">
            <DialogClose asChild>
              <Button variant="ghost">Keep Editing</Button>
            </DialogClose>
            <Button variant="secondary-flat-light" onClick={confirmCancel}>
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
